import axios from 'axios';

import { authBaseInstance, authBetProcessor } from '@/lib/api';
import { core } from '@/config';

/**
 * @template T
 * @typedef {import('axios').AxiosResponse<T>} AxiosResponse
 */

/**
 * @typedef {object} PlayerProfile
 * @property {string} currency
 * @property {string[]} currencies
 * @property {string} id
 * @property {string} name
 * @property {string} playerId
 * @property {string} favorite
 * @property {string} token
 * @property {boolean} guest
 */

const instance = axios.create({
  baseURL: core,
});

/**
 * @param {object} params
 * @param {string} params.apiKey
 * @param {string|null=} params.token
 * @param {string} params.visitorId
 * @param {boolean} params.force
 *
 * @returns {Promise<AxiosResponse<PlayerProfile>>}
 */
export async function getProfile(params) {
  /** @type {typeof params & {guest?: boolean}} */
  const ownParams = { ...params };

  const { apiKey, token } = params;

  if (!apiKey) {
    throw new Error(`params.apiKey is required for getProfile`);
  }

  if (!token) {
    // if no token — initialize guest access
    delete ownParams.token;

    ownParams.guest = true;
  }

  return instance.post('/public/api/profile', ownParams);
}

export function getBalance({ token, apiKey, currency, profileId }) {
  return authBaseInstance.get(
    `/public/api/profile-balance?token=${token}&apiKey=${apiKey}&currency=${currency}&profileId=${profileId}`,
  );
}

export function getSettings(id, params) {
  return instance.get(`/public/api/settings/${id}`, params);
}

export function createDemoPlayer(params) {
  return instance.post('/public/api/demoPartner/createDemoPlayer', params);
}

export function place(opts) {
  return authBetProcessor.post('/placebet/ordinary', opts);
}

export function placeExpress(opts) {
  return authBetProcessor.post('/placebet/express', opts);
}

export function getBetStatus(opts) {
  return authBetProcessor.get(`/placebet/results/${opts.id}`, opts);
}

export function placeBetBuilder(opts) {
  return authBetProcessor.post('/placebet/betbuilder/ordinary', opts);
}

export function getBetBuilderCoefficient({ outcomeIds = [], currency = '' }) {
  return authBetProcessor.get('/placebet/betbuilder/coefficient', {
    params: {
      currency,
      outcomeIds: outcomeIds,
    },
  });
}

export function getBetBuilderOutcomes(eventId = '') {
  return authBetProcessor.get(`/events/${eventId}/bet-builder`);
}
